import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClienteFormDialog } from '@/components/ClienteFormDialog';
import { maskPhone } from '@/lib/formatters';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Users, Plus, Edit, Trash2, Phone } from 'lucide-react';
import { useCanWrite } from '../hooks/useCanWrite';

export const Clientes = ({ user, onLogout }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const { canWrite, checkCanWrite } = useCanWrite();

  useEffect(() => {
    fetchCompany();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchClientes();
    }
  }, [selectedCompany]);

  const fetchCompany = async () => {
    try {
      const isFuncionario = user?.role === 'FUNCIONARIO' || user?.role === 'funcionario';
      if (isFuncionario && user?.company_id) {
        const stored = localStorage.getItem('company');
        if (stored) {
          setSelectedCompany(JSON.parse(stored));
        } else {
          const res = await axiosInstance.get(`/company/${user.company_id}`);
          setSelectedCompany(res.data);
          localStorage.setItem('company', JSON.stringify(res.data));
        }
        return;
      }
      const userId = user.user_id || user.id;
      const response = await axiosInstance.get(`/companies/${userId}`);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0]);
      }
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
      toast.error('Erro ao carregar empresa');
    }
  };

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/clientes/${selectedCompany.id}`);
      setClientes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cliente = null) => {
    setEditingCliente(cliente);
    setShowModal(true);
  };

  const handleDelete = async (clienteId) => {
    if (!checkCanWrite('excluir cliente')) return;
    if (!window.confirm('Deseja realmente excluir este cliente?')) return;

    try {
      await axiosInstance.delete(`/clientes/${clienteId}`);
      toast.success('Cliente excluído com sucesso!');
      fetchClientes();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const numbers = phone.replace(/\D/g, '');
    return numbers;
  };

  const openWhatsApp = (phone) => {
    const numbers = formatPhone(phone);
    window.open(`https://wa.me/55${numbers}`, '_blank');
  };

  // Funções de máscara agora são importadas de @/lib/formatters

  if (!selectedCompany) {
    return (
      <div className="min-h-screen gradient-bg flex">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 p-8">
          <Card className="glass border-white/10">
            <CardContent className="p-8 text-center">
              <p className="text-gray-400">Selecione uma empresa para gerenciar clientes</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg flex">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 p-8 overflow-auto">
        <SubscriptionCard />

        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Clientes</h1>
            <p className="text-gray-400">{selectedCompany.name}</p>
          </div>
          <Button
            onClick={() => {
              if (checkCanWrite('criar cliente')) {
                handleOpenModal();
              }
            }}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            disabled={!canWrite}
            title={!canWrite ? 'Assine para criar novos clientes' : ''}
          >
            <Plus className="mr-2" size={18} />
            Novo Cliente
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-white">Carregando...</div>
        ) : clientes.length === 0 ? (
          <Card className="glass border-white/10">
            <CardContent className="p-12 text-center">
              <Users className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-400">Nenhum cliente cadastrado ainda</p>
              <Button
                onClick={() => handleOpenModal()}
                className="mt-4 bg-purple-600 hover:bg-purple-700"
              >
                Cadastrar Primeiro Cliente
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Lista de Clientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 text-gray-400">Tipo</th>
                      <th className="text-left p-3 text-gray-400">Nome</th>
                      <th className="text-left p-3 text-gray-400">CPF/CNPJ</th>
                      <th className="text-left p-3 text-gray-400">WhatsApp</th>
                      <th className="text-left p-3 text-gray-400">Cidade</th>
                      <th className="text-right p-3 text-gray-400">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientes.map((cliente) => (
                      <tr key={cliente.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            cliente.tipo === 'PF' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'
                          }`}>
                            {cliente.tipo}
                          </span>
                        </td>
                        <td className="p-3 text-white">
                          {cliente.tipo === 'PF' ? cliente.nome : cliente.nome_fantasia || cliente.razao_social}
                        </td>
                        <td className="p-3 text-gray-300">
                          {cliente.tipo === 'PF' ? cliente.cpf : cliente.cnpj}
                        </td>
                        <td className="p-3">
                          {cliente.whatsapp && (
                            <button
                              onClick={() => openWhatsApp(cliente.whatsapp)}
                              className="text-green-400 hover:text-green-300 flex items-center"
                            >
                              <Phone size={16} className="mr-1" />
                              {maskPhone(cliente.whatsapp)}
                            </button>
                          )}
                        </td>
                        <td className="p-3 text-gray-300">{cliente.cidade}</td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenModal(cliente)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(cliente.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <ClienteFormDialog
          open={showModal}
          onOpenChange={setShowModal}
          cliente={editingCliente}
          companyId={selectedCompany.id}
          onSaved={fetchClientes}
        />
      </div>
    </div>
  );
};
